// Entrega de un pedido RETIRA (R8) — se renderiza DENTRO del detalle del pedido
// cuando el encargado abre un pedido despachado con método RETIRA.
//
// Flujo: si faltan las etiquetas, las imprime acá mismo → escanea cada bulto
// (todos, obligatorio) → confirma → el pedido pasa a ENTREGADO (se mueve a
// pedidos_entregados y sale del tablero) vía entregarPedidoDespachado.

import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useApp } from "../../../context/AppContext";
import QrScanner from "../../../components/QrScanner";
import { entregarPedidoDespachado } from "../services/pedidosFS";
import { generarEImprimirEtiquetas } from "../services/etiquetasPDF";

export default function EntregaRetiraDetalle({ pedido }) {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { toast, haptics } = useApp();

  const totalBultos = Math.max(1, Number(pedido.bultos) || 1);
  const [escaneados, setEscaneados] = useState(new Set());
  const [error, setError]           = useState("");
  const [saving, setSaving]         = useState(false);
  const [printedNow, setPrintedNow] = useState(false);
  const [imprimiendo, setImprimiendo] = useState(false);

  const etiquetasListas = !!pedido.etiquetasImpresas || printedNow;

  const handleScan = useCallback((raw) => {
    setError("");
    const texto = typeof raw === "string" ? raw : raw?.text || raw?.decodedText || String(raw);
    const match = texto.match(/^(.+)\|(\d+)\/(\d+)$/);
    if (!match) { setError(`QR no reconocido: "${texto.slice(0, 40)}"`); return; }
    const [, pid, bultoN] = match;
    if (pid !== pedido.id) { setError(`Este bulto es de otro pedido (#${pid})`); return; }
    const n = Number(bultoN);
    if (escaneados.has(n)) { setError(`Bulto ${n} ya escaneado`); return; }
    setEscaneados(prev => new Set([...prev, n]));
  }, [pedido.id, escaneados]);

  const todos = escaneados.size === totalBultos;

  async function imprimir() {
    setImprimiendo(true);
    try {
      await generarEImprimirEtiquetas([{
        pedidoId: pedido.id,
        numero: pedido.numero || pedido.id,
        cliente: pedido.cliente || "",
        direccion: pedido.metodoEntrega || "RETIRA",
        localidad: pedido.deposito || "R8",
        bultos: totalBultos,
      }]);
      setPrintedNow(true);
    } catch (e) {
      toast.error("No se pudieron imprimir las etiquetas");
    } finally {
      setImprimiendo(false);
    }
  }

  async function confirmar() {
    setSaving(true);
    try {
      await entregarPedidoDespachado(pedido.id, {
        uid: user?.uid || null,
        nombre: profile?.nombre || profile?.displayName || null,
        bultosEscaneados: [...escaneados].sort((a, b) => a - b),
      });
      haptics?.success?.();
      toast.success("Pedido entregado ✓");
      navigate("/pedidos");
    } catch (e) {
      console.error("[EntregaRetiraDetalle] error:", e);
      haptics?.error?.();
      toast.error(e?.message || "No se pudo registrar la entrega");
      setSaving(false);
    }
  }

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "100px" }}>

      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "12px 16px 14px" }}>
        <button
          onClick={() => navigate("/pedidos")}
          style={{ background: "none", border: "none", color: "#64748b", fontSize: "0.85rem", cursor: "pointer", padding: 0, marginBottom: "10px", display: "flex", alignItems: "center", gap: "4px" }}
        >
          ← Volver
        </button>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
          <div>
            <p style={{ margin: 0, fontSize: "0.7rem", fontWeight: 700, color: "#94a3b8", letterSpacing: "0.07em", textTransform: "uppercase" }}>
              Entrega · Retira · #{pedido.numero || pedido.id}
            </p>
            <h1 style={{ margin: "3px 0 0", fontSize: "1.3rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
              {pedido.cliente || "—"}
            </h1>
          </div>
          <span style={{ flexShrink: 0, background: "#fef3c7", color: "#92400e", fontSize: "0.68rem", fontWeight: 700, padding: "4px 10px", borderRadius: "999px", letterSpacing: "0.05em", marginTop: "4px" }}>
            PENDIENTE DE ENTREGA
          </span>
        </div>
      </div>

      <div style={{ padding: "16px" }}>

        {/* Paso 1: imprimir etiquetas si faltan */}
        {!etiquetasListas ? (
          <div style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: "14px", padding: "16px" }}>
            <p style={{ margin: "0 0 4px", fontSize: "0.95rem", fontWeight: 700, color: "#92400e" }}>
              ⚠️ Faltan imprimir las etiquetas
            </p>
            <p style={{ margin: "0 0 14px", fontSize: "0.83rem", color: "#a16207" }}>
              Este pedido tiene {totalBultos} bulto{totalBultos !== 1 ? "s" : ""}. Imprimí las etiquetas, pegalas y después escaneá cada una.
            </p>
            <button
              onClick={imprimir}
              disabled={imprimiendo}
              style={{
                width: "100%", padding: "13px", borderRadius: "12px", border: "none",
                background: imprimiendo ? "#e2e8f0" : "#1d4ed8", color: imprimiendo ? "#94a3b8" : "#fff",
                fontWeight: 700, fontSize: "0.95rem", cursor: imprimiendo ? "not-allowed" : "pointer",
              }}
            >
              {imprimiendo ? "Imprimiendo…" : `🖨️ Imprimir ${totalBultos} etiqueta${totalBultos !== 1 ? "s" : ""}`}
            </button>
          </div>
        ) : (
          <>
            {/* Paso 2: escanear bultos */}
            <p style={{ margin: "0 0 10px", fontSize: "0.7rem", fontWeight: 700, color: "#64748b", letterSpacing: "0.07em", textTransform: "uppercase" }}>
              Escaneá los {totalBultos} bulto{totalBultos !== 1 ? "s" : ""}
            </p>

            {/* Progreso */}
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
              {Array.from({ length: totalBultos }, (_, i) => i + 1).map(n => (
                <div key={n} style={{
                  width: "40px", height: "40px", borderRadius: "10px",
                  background: escaneados.has(n) ? "#16a34a" : "#f1f5f9",
                  color: escaneados.has(n) ? "#fff" : "#94a3b8",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: "15px",
                  border: `1.5px solid ${escaneados.has(n) ? "#16a34a" : "#e2e8f0"}`,
                }}>{escaneados.has(n) ? "✓" : n}</div>
              ))}
            </div>
            <p style={{ margin: "0 0 12px", fontSize: "0.85rem", color: "#64748b" }}>
              {escaneados.size}/{totalBultos} escaneados
            </p>

            {error && (
              <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: "10px", padding: "10px 14px", color: "#dc2626", fontSize: "0.83rem", fontWeight: 600, marginBottom: "12px" }}>
                ⚠️ {error}
              </div>
            )}

            {!todos ? (
              <QrScanner onScanSuccess={handleScan} qrbox={{ width: 240, height: 240 }} />
            ) : (
              <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "14px", padding: "18px", textAlign: "center" }}>
                <div style={{ fontSize: "32px", marginBottom: "6px" }}>✅</div>
                <p style={{ margin: 0, fontWeight: 700, color: "#166534", fontSize: "1rem" }}>
                  Todos los bultos escaneados
                </p>
              </div>
            )}

            {/* Reimprimir por si hace falta */}
            <button
              onClick={imprimir}
              disabled={imprimiendo}
              style={{ marginTop: "14px", background: "none", border: "none", color: "#64748b", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", textDecoration: "underline", padding: 0 }}
            >
              🖨️ Reimprimir etiquetas
            </button>
          </>
        )}
      </div>

      {/* Botón fijo: confirmar entrega */}
      {etiquetasListas && (
        <div className="cta-fija" style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          padding: "12px 16px 20px", background: "#fff",
          borderTop: "1px solid #e2e8f0", boxShadow: "0 -4px 16px rgba(0,0,0,0.06)",
        }}>
          {!todos && (
            <p style={{ margin: "0 0 8px", fontSize: "0.78rem", color: "#b45309", textAlign: "center" }}>
              Escaneá todos los bultos para confirmar
            </p>
          )}
          <button
            onClick={confirmar}
            disabled={!todos || saving}
            style={{
              width: "100%", padding: "16px", borderRadius: "14px", border: "none",
              fontWeight: 700, fontSize: "1.05rem",
              background: (todos && !saving) ? "#0f172a" : "#e2e8f0",
              color: (todos && !saving) ? "#fff" : "#94a3b8",
              cursor: (todos && !saving) ? "pointer" : "not-allowed",
            }}
          >
            {saving ? "Registrando…" : "✅ Confirmar entrega"}
          </button>
        </div>
      )}
    </div>
  );
}
